import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DashboardStatistics, ChartData } from '@console/utils';
import { dashboardApi, ExpiringContract, ContractConsumption } from '@/lib/api/dashboard';

export function useDashboard() {
  const { t } = useTranslation();
  const [statistics, setStatistics] = useState<DashboardStatistics | null>(null);
  const [expiringContracts, setExpiringContracts] = useState<ExpiringContract[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingExpiring, setIsLoadingExpiring] = useState(false);

  const fetchStatistics = useCallback(async () => {
    try {
      setIsLoadingStats(true);
      const data = await dashboardApi.getStatistics();
      setStatistics(data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('dashboard.fetchStatsError'));
    } finally {
      setIsLoadingStats(false);
    }
  }, [t]);

  const fetchExpiringContracts = useCallback(
    async (days?: number) => {
      try {
        setIsLoadingExpiring(true);
        const data = await dashboardApi.getExpiringContracts(days);
        setExpiringContracts(data);
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('dashboard.fetchExpiringError'));
      } finally {
        setIsLoadingExpiring(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchStatistics();
    fetchExpiringContracts();
  }, [fetchStatistics, fetchExpiringContracts]);

  return {
    statistics,
    expiringContracts,
    isLoadingStats,
    isLoadingExpiring,
    refetchStatistics: fetchStatistics,
    refetchExpiringContracts: fetchExpiringContracts,
  };
}

export function useDashboardContractData(
  contractId?: string,
  startDate?: string,
  endDate?: string
) {
  const { t } = useTranslation();
  const [consumption, setConsumption] = useState<ContractConsumption | null>(null);
  const [ticketsChart, setTicketsChart] = useState<ChartData | null>(null);
  const [hoursChart, setHoursChart] = useState<ChartData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchContractData = useCallback(async () => {
    if (!contractId) {
      setConsumption(null);
      setTicketsChart(null);
      setHoursChart(null);
      return;
    }

    try {
      setIsLoading(true);
      const [consumptionData, ticketsData, hoursData] = await Promise.all([
        dashboardApi.getContractConsumption(contractId),
        dashboardApi.getTicketsChart(contractId, startDate, endDate),
        dashboardApi.getHoursChart(contractId, startDate, endDate),
      ]);

      setConsumption(consumptionData);
      setTicketsChart(ticketsData);
      setHoursChart(hoursData);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('dashboard.fetchContractDataError'));
    } finally {
      setIsLoading(false);
    }
  }, [contractId, startDate, endDate, t]);

  useEffect(() => {
    fetchContractData();
  }, [fetchContractData]);

  return {
    consumption,
    ticketsChart,
    hoursChart,
    isLoading,
    refetch: fetchContractData,
  };
}
